import type { DataGridFilterConfig, DataGridFilterValue } from '@ibsys/kit';
import type {
  AttributeFilter,
  AttributeFilterBoolean,
  AttributeFilterDateRange,
  AttributeFilterNumeric,
  AttributeFilterSelect,
  AttributeFilterText,
  AttributeFilterTimestampRange,
} from './api/types';

/**
 * Вид условия по значению атрибута — тот же дискриминатор, что у конфига отбора `DataGrid`.
 * Правило «атрибут → вид условия» одно на все таблицы и панели фильтров
 * (`getAttributeValueFilter` в `entities/attributes`), поэтому и здесь вид условия берётся
 * из `config.type`, а не выводится по типу атрибута заново.
 */
export type AttributeFilterKind = DataGridFilterConfig['type'];

/**
 * Поля значений атрибутного фильтра, читаемые по виду условия. Элемент `AttributeFilter` —
 * размеченное объединение по набору полей, а вид условия известен снаружи (из конфига отбора),
 * поэтому чтение идёт через одну общую форму, а не через сужение объединения.
 */
type AttributeFilterFields = Partial<
  AttributeFilterSelect &
    AttributeFilterNumeric &
    AttributeFilterText &
    AttributeFilterBoolean &
    AttributeFilterDateRange &
    AttributeFilterTimestampRange
>;

/** Границы диапазона в конверте `DataGrid` (`integer` / `time-range` / `*-range`) */
type DataGridRangeValue = { from?: unknown; to?: unknown };

const asRange = (value: unknown): DataGridRangeValue =>
  typeof value === 'object' && value !== null ? (value as DataGridRangeValue) : {};

/** Число из конверта грида; всё остальное (в том числе пустая строка) — граница не задана */
const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** Непустая строка из конверта грида; всё остальное — значение не задано */
const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/**
 * Атрибутный фильтр бэкенда — в конверт значения отбора `DataGrid`.
 * Незаполненный фильтр даёт `undefined`: у грида «фильтр не задан» — это отсутствие значения,
 * а не пустое значение.
 */
export const attributeFilterToDataGridValue = (
  filter: AttributeFilter,
  kind: AttributeFilterKind,
): DataGridFilterValue | undefined => {
  const fields: AttributeFilterFields = filter;
  switch (kind) {
    case 'select': {
      const first = fields.values?.[0];
      return first === undefined ? undefined : { value: first };
    }
    case 'multiselect':
      return fields.values?.length ? { value: fields.values } : undefined;
    case 'integer':
    case 'time-range':
      return fields.min === undefined && fields.max === undefined
        ? undefined
        : { value: { from: fields.min, to: fields.max } };
    case 'date-range':
    case 'timestamp-range':
      return fields.dateFrom || fields.dateTo ? { value: { from: fields.dateFrom, to: fields.dateTo } } : undefined;
    case 'date':
    case 'timestamp':
      // Одиночная дата хранится тем же интервалом с совпадающими границами
      return fields.dateFrom ? { value: fields.dateFrom } : undefined;
    case 'boolean':
      return fields.bool === undefined ? undefined : { value: fields.bool };
    case 'text':
      return fields.text ? { value: fields.text } : undefined;
  }
};

/**
 * Конверт значения отбора `DataGrid` — в атрибутный фильтр бэкенда.
 * Форма результата определяется видом условия, а не тем, что лежало в фильтре раньше.
 * `next === undefined` (сброс отбора) даёт пустой фильтр того же вида — тот же результат,
 * что и у только что добавленного атрибута.
 *
 * Оператор отбора (`notEmpty`) контракт атрибутных фильтров не выражает, поэтому значение с
 * оператором читается как пустое. Тумблер «не пусто» в этих полях и не включается —
 * `enableNotEmptyFilter` в конфиг не попадает.
 */
export const dataGridValueToAttributeFilter = (
  path: string,
  kind: AttributeFilterKind,
  next: DataGridFilterValue | undefined,
): AttributeFilter => {
  const value = next?.operator === undefined ? next?.value : undefined;
  switch (kind) {
    case 'select': {
      const single = asString(value) ?? asNumber(value);
      return { path, values: single === undefined ? [] : [String(single)] };
    }
    case 'multiselect': {
      if (Array.isArray(value)) return { path, values: (value as unknown[]).map((v) => String(v)) };
      const single = asString(value) ?? asNumber(value);
      return { path, values: single === undefined ? [] : [String(single)] };
    }
    case 'integer':
    case 'time-range': {
      const range = asRange(value);
      return { path, min: asNumber(range.from), max: asNumber(range.to) };
    }
    case 'date-range':
    case 'timestamp-range': {
      const range = asRange(value);
      return { path, dateFrom: asString(range.from), dateTo: asString(range.to) };
    }
    case 'date':
    case 'timestamp': {
      const single = asString(value);
      return { path, dateFrom: single, dateTo: single };
    }
    case 'boolean':
      // Отбор по чекбоксу применяется сразу после добавления: снятое значение — это «Нет»
      return { path, bool: value === true };
    case 'text':
      return { path, text: asString(value) ?? '' };
  }
};

/**
 * Проверяет, что фильтр содержит хотя бы одно осмысленное значение.
 * Пустые фильтры не отправляются на бэкенд (см. IL «Фильтрация по атрибутам дашборда»):
 * пустой `values: []` бэкенд трактует как «ни одно значение не подходит» и возвращает
 * пустую выборку, а не игнорирует условие.
 *
 * - `values` — хотя бы один элемент;
 * - `text` — непустая строка после trim;
 * - `bool` — применяется всегда, если поле присутствует;
 * - `dateFrom`/`dateTo` — хотя бы одна граница задана;
 * - `min`/`max` — хотя бы одна граница задана.
 */
export const isFilterFilled = (f: AttributeFilter): boolean => {
  if ('values' in f) {
    return f.values.length > 0;
  }
  if ('text' in f) {
    return f.text.trim() !== '';
  }
  if ('bool' in f) {
    return true;
  }
  if ('dateFrom' in f || 'dateTo' in f) {
    const range = f as AttributeFilterDateRange | AttributeFilterTimestampRange;
    return Boolean(range.dateFrom) || Boolean(range.dateTo);
  }
  const numeric = f as AttributeFilterNumeric;
  return numeric.min !== undefined || numeric.max !== undefined;
};

/** Отбрасывает незаполненные атрибутные фильтры — только они и уходят в запрос */
export const pickFilledAttributeFilters = (filters: AttributeFilter[]): AttributeFilter[] =>
  filters.filter(isFilterFilled);

/**
 * Сериализует список атрибутных фильтров в строку для query-параметра `attribute-filters`.
 * Возвращает `null`, если нет ни одного заполненного фильтра — параметр в этом случае не передаётся.
 */
export const serializeAttributeFilters = (filters: AttributeFilter[]): string | null => {
  const filled = pickFilledAttributeFilters(filters);
  if (filled.length === 0) return null;
  return JSON.stringify(filled);
};
